import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import jurisdictionService from '../../services/jurisdictionService';
import { useAmendApplication, useSendAmendmentEmail, useApproveAmendment } from '../../hooks/useFilingQueue';
import { useAttorney } from '../../context/AttorneyContext';
import './FileNowModal.css';

// Country flag emojis
const countryFlags = {
  AU: '🇦🇺',
  US: '🇺🇸',
  EU: '🇪🇺',
  UK: '🇬🇧',
  NZ: '🇳🇿',
  CA: '🇨🇦',
  SG: '🇸🇬'
};

/**
 * File Now Modal Component
 * Complete filing review with all application data for attorney
 */
function FileNowModal({ application, jurisdiction, onClose, onMarkFiled, isLoading, viewMode = false }) {
  const { t } = useTranslation('attorney');
  const { isAttorneyAdmin, isAdmin } = useAttorney();
  const canEdit = isAttorneyAdmin || isAdmin;

  const [ipOfficeRef, setIpOfficeRef] = useState('');
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Email compose state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [includeChangesSummary, setIncludeChangesSummary] = useState(true);
  const [includeApprovalButtons, setIncludeApprovalButtons] = useState(true);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Mutations
  const amendMutation = useAmendApplication();
  const sendEmailMutation = useSendAmendmentEmail();
  const approveMutation = useApproveAmendment();

  // Fetch jurisdiction details
  const { data: jurisdictionData } = useQuery({
    queryKey: ['jurisdiction', jurisdiction.jurisdiction],
    queryFn: () => jurisdictionService.getJurisdiction(jurisdiction.jurisdiction)
  });

  const handleCopyAppId = () => {
    navigator.clipboard.writeText(application.applicationId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyText = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (ipOfficeRef.trim()) {
      onMarkFiled(ipOfficeRef.trim());
    }
  };

  // --- Edit mode helpers ---
  const startEditing = () => {
    // Initialize edit data from current effective values (amendments overlay applied)
    const effectiveTrademark = { ...(application.trademark || {}), ...(application.amendments?.trademark || {}) };
    const effectiveApplicant = { ...(application.applicant || {}), ...(application.amendments?.applicant || {}) };
    const effectiveClassification = { ...(application.classification || {}), ...(application.amendments?.classification || {}) };

    setEditData({
      trademark: { name: effectiveTrademark.name || '', type: effectiveTrademark.type || '', description: effectiveTrademark.description || '' },
      applicant: {
        applicantName: effectiveApplicant.applicantName || '',
        applicantType: effectiveApplicant.applicantType || '',
        businessIdentifier: effectiveApplicant.businessIdentifier || '',
        email: effectiveApplicant.email || '',
        phone: effectiveApplicant.phone || '',
        address: {
          street: effectiveApplicant.address?.street || '',
          city: effectiveApplicant.address?.city || '',
          state: effectiveApplicant.address?.state || '',
          postalCode: effectiveApplicant.address?.postalCode || '',
          country: effectiveApplicant.address?.country || ''
        }
      },
      classification: {
        selectedClasses: (effectiveClassification.selectedClasses || []).map(cls => ({
          classNumber: cls.classNumber || cls.number || cls.class,
          className: cls.className || cls.name || '',
          goodsServices: [...(cls.goodsServices || [])].map(gs => typeof gs === 'string' ? gs : gs.description || gs.term || '')
        }))
      }
    });
    setEditNote('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditData(null);
    setEditNote('');
  };

  const computeChanges = () => {
    if (!editData) return [];
    const changes = [];
    const orig = application;

    // Trademark changes
    const origTm = { ...(orig.trademark || {}), ...(orig.amendments?.trademark || {}) };
    if (editData.trademark.name !== (origTm.name || '')) {
      changes.push({ section: 'trademark', field: 'name', oldValue: origTm.name || '', newValue: editData.trademark.name });
    }
    if (editData.trademark.type !== (origTm.type || '')) {
      changes.push({ section: 'trademark', field: 'type', oldValue: origTm.type || '', newValue: editData.trademark.type });
    }
    if (editData.trademark.description !== (origTm.description || '')) {
      changes.push({ section: 'trademark', field: 'description', oldValue: origTm.description || '', newValue: editData.trademark.description });
    }

    // Applicant changes — check all fields
    const origApp = { ...(orig.applicant || {}), ...(orig.amendments?.applicant || {}) };
    const applicantFields = ['applicantName', 'applicantType', 'businessIdentifier', 'email', 'phone'];
    applicantFields.forEach(field => {
      if (editData.applicant[field] !== (origApp[field] || '')) {
        changes.push({ section: 'applicant', field, oldValue: origApp[field] || '', newValue: editData.applicant[field] });
      }
    });

    // Address sub-fields
    const origAddr = origApp.address || {};
    const editAddr = editData.applicant.address || {};
    const addressFields = ['street', 'city', 'state', 'postalCode', 'country'];
    addressFields.forEach(field => {
      if ((editAddr[field] || '') !== (origAddr[field] || '')) {
        changes.push({ section: 'applicant', field: `address.${field}`, oldValue: origAddr[field] || '', newValue: editAddr[field] || '' });
      }
    });

    // Classification changes (simplified — check if goods/services arrays differ)
    const origCls = { ...(orig.classification || {}), ...(orig.amendments?.classification || {}) };
    const origClasses = origCls.selectedClasses || [];
    const newClasses = editData.classification.selectedClasses || [];
    if (JSON.stringify(origClasses) !== JSON.stringify(newClasses)) {
      changes.push({ section: 'classification', field: 'selectedClasses', oldValue: origClasses, newValue: newClasses });
    }

    return changes;
  };

  const saveAmendments = async () => {
    if (!editData) return;
    const changes = computeChanges();
    if (changes.length === 0) {
      cancelEditing();
      return;
    }

    setIsSaving(true);
    try {
      await amendMutation.mutateAsync({
        applicationId: application.id,
        data: {
          amendments: editData,
          note: editNote,
          changes
        }
      });
      setIsEditing(false);
      setEditData(null);
      // Pre-populate email tab with defaults only if not already drafted
      if (!emailSubject) {
        const appId = application.applicationId || application.id;
        setEmailSubject(`Update on your trademark application — ${appId}`);
      }
      if (!emailBody) {
        setEmailBody(`Hi {firstName},\n\nI've completed my review of your trademark application for {trademarkName} and have made some updates to strengthen your filing.\n\n${editNote || 'Please review the changes summary below.'}\n\nIf you have any questions, please don't hesitate to reach out.`);
      }
    } catch (err) {
      console.error('Failed to save amendments:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendEmail = async () => {
    setIsSendingEmail(true);
    try {
      // Get latest amendment ID
      const latestAmendment = application.amendmentHistory?.[application.amendmentHistory.length - 1];
      const changesSummary = computeChangesSummaryText();

      await sendEmailMutation.mutateAsync({
        applicationId: application.id,
        data: {
          amendmentId: latestAmendment?.id || null,
          subject: emailSubject,
          emailBody,
          changesSummary,
          includeChangesSummary,
          includeApprovalButtons
        }
      });
      setEmailSent(true);
    } catch (err) {
      console.error('Failed to send email:', err);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const computeChangesSummaryText = () => {
    const history = application.amendmentHistory || [];
    // Collect changes from ALL unapproved history entries
    const allChanges = [];
    for (const entry of history) {
      if (entry.customerResponse === 'approved') continue;
      if (entry.changes) allChanges.push(...entry.changes);
    }
    if (allChanges.length === 0) return [];
    const summaryLines = allChanges.map(c => {
      if (c.section === 'trademark' && c.field === 'name') return `Trademark name: updated to "${c.newValue}"`;
      if (c.section === 'trademark' && c.field === 'type') return `Trademark type: updated to "${c.newValue}"`;
      if (c.section === 'trademark' && c.field === 'description') return `Trademark description: updated`;
      if (c.section === 'applicant' && c.field === 'applicantName') return `Applicant name: updated to "${c.newValue}"`;
      if (c.section === 'applicant' && c.field === 'applicantType') return `Applicant type: updated to "${c.newValue}"`;
      if (c.section === 'applicant' && c.field === 'email') return `Applicant email: updated to "${c.newValue}"`;
      if (c.section === 'applicant' && c.field === 'phone') return `Applicant phone: updated to "${c.newValue}"`;
      if (c.section === 'applicant' && c.field === 'businessIdentifier') return `Business identifier: updated to "${c.newValue}"`;
      if (c.section === 'applicant' && c.field.startsWith('address.')) return `Applicant address: updated`;
      if (c.section === 'classification') return `Classes & goods/services: updated`;
      return `${c.section}.${c.field}: updated`;
    });
    // Deduplicate (e.g. multiple address fields → single "Applicant address: updated")
    return [...new Set(summaryLines)];
  };

  // Edit helpers for classification
  const addServiceToClass = (classIndex, service) => {
    if (!service.trim() || !editData) return;
    const newClasses = [...editData.classification.selectedClasses];
    newClasses[classIndex] = { ...newClasses[classIndex], goodsServices: [...newClasses[classIndex].goodsServices, service.trim()] };
    setEditData({ ...editData, classification: { ...editData.classification, selectedClasses: newClasses } });
  };

  const removeServiceFromClass = (classIndex, serviceIndex) => {
    if (!editData) return;
    const newClasses = [...editData.classification.selectedClasses];
    const newServices = [...newClasses[classIndex].goodsServices];
    newServices.splice(serviceIndex, 1);
    newClasses[classIndex] = { ...newClasses[classIndex], goodsServices: newServices };
    setEditData({ ...editData, classification: { ...editData.classification, selectedClasses: newClasses } });
  };

  const removeClass = (classIndex) => {
    if (!editData) return;
    const newClasses = [...editData.classification.selectedClasses];
    newClasses.splice(classIndex, 1);
    setEditData({ ...editData, classification: { ...editData.classification, selectedClasses: newClasses } });
  };

  const addNewClass = () => {
    if (!editData) return;
    const newClasses = [...editData.classification.selectedClasses, { classNumber: '', className: '', goodsServices: [] }];
    setEditData({ ...editData, classification: { ...editData.classification, selectedClasses: newClasses } });
  };

  const flag = countryFlags[jurisdiction.jurisdiction] || '🌐';
  const portal = jurisdictionData?.portal || {};

  // Effective data (original + amendments overlay)
  const trademark = useMemo(() => ({
    ...(application.trademark || {}),
    ...(application.amendments?.trademark || {})
  }), [application]);
  const applicant = useMemo(() => ({
    ...(application.applicant || {}),
    ...(application.amendments?.applicant || {})
  }), [application]);
  const classification = useMemo(() => ({
    ...(application.classification || {}),
    ...(application.amendments?.classification || {})
  }), [application]);

  // Format address
  const formatAddress = (addr) => {
    if (!addr) return 'Not provided';
    const parts = [
      addr.street,
      addr.city,
      addr.state,
      addr.postalCode,
      addr.country
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Not provided';
  };

  return (
    <div className="file-now-modal-overlay" onClick={onClose}>
      <div className="file-now-modal file-now-modal-large" onClick={e => e.stopPropagation()}>
        <div className="file-now-modal-header">
          <button className="file-now-modal-close" onClick={onClose}>✕</button>
          <h2>
            {flag} {viewMode
              ? t('attorney.fileNowModal.viewTitle', 'Filing Details - {{country}}', {
                  country: jurisdictionData?.name || jurisdiction.jurisdiction
                })
              : t('attorney.fileNowModal.title', 'File in {{country}}', {
                  country: jurisdictionData?.name || jurisdiction.jurisdiction
                })
            }
          </h2>
          <p className="file-now-modal-subtitle">
            {trademark.name} • {application.applicationId}
            {viewMode && jurisdiction.ipOfficeRef && (
              <> • <strong>Ref: {jurisdiction.ipOfficeRef}</strong></>
            )}
          </p>
        </div>

        {/* Tabs */}
        <div className="file-now-tabs">
          <button
            className={`file-now-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`file-now-tab ${activeTab === 'trademark' ? 'active' : ''}`}
            onClick={() => setActiveTab('trademark')}
          >
            Trademark
          </button>
          <button
            className={`file-now-tab ${activeTab === 'applicant' ? 'active' : ''}`}
            onClick={() => setActiveTab('applicant')}
          >
            Applicant
          </button>
          <button
            className={`file-now-tab ${activeTab === 'classes' ? 'active' : ''}`}
            onClick={() => setActiveTab('classes')}
          >
            Classes & Goods
          </button>
          <button
            className={`file-now-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Info
          </button>
          {canEdit && (
            <button
              className={`file-now-tab ${activeTab === 'email' ? 'active' : ''}`}
              onClick={() => setActiveTab('email')}
            >
              ✉ Send to Customer
            </button>
          )}
          <button
            className={`file-now-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            📋 History
          </button>
          {application.amendmentStatus && application.amendmentStatus !== 'approved' && (
            <button
              className={`file-now-tab ${activeTab === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveTab('pending')}
            >
              ⏳ Pending
            </button>
          )}

          {/* Edit toggle button */}
          {canEdit && !isEditing && (
            <button className="file-now-tab file-now-edit-btn" onClick={startEditing}>
              ✏ Edit
            </button>
          )}
          {isEditing && (
            <button className="file-now-tab file-now-edit-btn editing" onClick={cancelEditing}>
              ✕ Cancel Edit
            </button>
          )}
        </div>

        <div className="file-now-modal-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {/* Application Reference */}
              <section className="file-now-section">
                <h3>Application Reference</h3>
                <p className="section-description">
                  Use this reference in all IP Office forms:
                </p>
                <div className="reference-box">
                  <span className="reference-value">{application.applicationId}</span>
                  <button className="btn-copy" onClick={handleCopyAppId}>
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </section>

              {/* Quick Summary */}
              <section className="file-now-section">
                <h3>Filing Summary</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Trademark</span>
                    <span className="summary-value">{trademark.name || 'Unknown'}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Type</span>
                    <span className="summary-value">{trademark.type || 'Figurative'}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Applicant</span>
                    <span className="summary-value">{applicant.applicantName || application.clientName || 'Unknown'}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Classes</span>
                    <span className="summary-value">
                      {classification.selectedClasses?.length > 0
                        ? classification.selectedClasses.map(cls => cls.classNumber || cls.number || cls.class).filter(Boolean).join(', ')
                        : classification.niceClasses?.length > 0
                          ? classification.niceClasses.join(', ')
                          : 'Not specified'}
                    </span>
                  </div>
                </div>
              </section>

              {/* IP Office Portal */}
              <section className="file-now-section">
                <h3>IP Office Portal</h3>
                <div className="portal-info">
                  <span className="portal-flag">{flag}</span>
                  <span className="portal-name">{jurisdictionData?.office || 'IP Office'}</span>
                </div>
                {portal.url && (
                  <a
                    href={portal.filingUrl || portal.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-open-portal"
                  >
                    Open Portal →
                  </a>
                )}
              </section>

              {/* Instructions */}
              <section className="file-now-section">
                <h3>Filing Steps</h3>
                <ol className="instructions-list">
                  <li>Log in to {jurisdictionData?.office || 'IP Office'} portal</li>
                  <li>Start new trademark application</li>
                  <li>Enter "<strong>{application.applicationId}</strong>" in "Your Reference" field</li>
                  <li>Enter applicant details (see Applicant tab)</li>
                  <li>Upload trademark image (see Trademark tab)</li>
                  <li>Enter goods/services (see Classes tab)</li>
                  <li>Complete payment on IP Office</li>
                  <li>Note the {jurisdictionData?.office || 'office'} reference number</li>
                </ol>
              </section>
            </>
          )}

          {/* Trademark Tab */}
          {activeTab === 'trademark' && (
            <>
              <section className="file-now-section">
                <h3>Trademark Details</h3>
                <div className="data-grid">
                  <div className="data-row">
                    <span className="data-label">Name</span>
                    <span className="data-value">{trademark.name || 'Not provided'}</span>
                    <button className="btn-copy-small" onClick={() => handleCopyText(trademark.name)}>Copy</button>
                  </div>
                  <div className="data-row">
                    <span className="data-label">Type</span>
                    <span className="data-value">{trademark.type || 'Figurative'}</span>
                  </div>
                  {trademark.description && (
                    <div className="data-row">
                      <span className="data-label">Description</span>
                      <span className="data-value">{trademark.description}</span>
                      <button className="btn-copy-small" onClick={() => handleCopyText(trademark.description)}>Copy</button>
                    </div>
                  )}
                  {trademark.colors && (
                    <div className="data-row">
                      <span className="data-label">Colors</span>
                      <span className="data-value">{trademark.colors}</span>
                    </div>
                  )}
                </div>
              </section>

              <section className="file-now-section">
                <h3>Trademark Image</h3>
                {trademark.imageUrl ? (
                  <div className="trademark-image-container">
                    <img
                      src={trademark.imageUrl}
                      alt={trademark.name}
                      className="trademark-image-preview"
                    />
                    <div className="trademark-image-actions">
                      <a
                        href={trademark.imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-download"
                      >
                        Download Image
                      </a>
                      <span className="file-name">{trademark.fileName || 'trademark-image.png'}</span>
                    </div>
                  </div>
                ) : (
                  <p className="no-data">No trademark image available</p>
                )}
              </section>
            </>
          )}

          {/* Applicant Tab */}
          {activeTab === 'applicant' && (
            <>
              <section className="file-now-section">
                <h3>Applicant Details</h3>
                {isEditing && editData ? (
                  <div className="data-grid edit-mode">
                    <div className="data-row">
                      <span className="data-label">Name</span>
                      <input
                        className="edit-input"
                        value={editData.applicant.applicantName}
                        onChange={(e) => setEditData({...editData, applicant: {...editData.applicant, applicantName: e.target.value}})}
                      />
                      {editData.applicant.applicantName !== (applicant.applicantName || '') && (
                        <span className="amendment-badge">Changed from: "{applicant.applicantName}"</span>
                      )}
                    </div>
                    <div className="data-row">
                      <span className="data-label">Type</span>
                      <select
                        className="edit-input"
                        value={editData.applicant.applicantType}
                        onChange={(e) => setEditData({...editData, applicant: {...editData.applicant, applicantType: e.target.value}})}
                      >
                        <option value="">Select type</option>
                        <option value="individual">Individual</option>
                        <option value="business">Business</option>
                        <option value="company">Company</option>
                        <option value="trust">Trust</option>
                        <option value="partnership">Partnership</option>
                      </select>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Business ID</span>
                      <input
                        className="edit-input"
                        value={editData.applicant.businessIdentifier}
                        onChange={(e) => setEditData({...editData, applicant: {...editData.applicant, businessIdentifier: e.target.value}})}
                      />
                    </div>
                    <div className="data-row">
                      <span className="data-label">Street</span>
                      <input
                        className="edit-input"
                        value={editData.applicant.address.street}
                        onChange={(e) => setEditData({...editData, applicant: {...editData.applicant, address: {...editData.applicant.address, street: e.target.value}}})}
                      />
                    </div>
                    <div className="data-row">
                      <span className="data-label">City</span>
                      <input
                        className="edit-input"
                        value={editData.applicant.address.city}
                        onChange={(e) => setEditData({...editData, applicant: {...editData.applicant, address: {...editData.applicant.address, city: e.target.value}}})}
                      />
                    </div>
                    <div className="data-row">
                      <span className="data-label">State</span>
                      <input
                        className="edit-input"
                        value={editData.applicant.address.state}
                        onChange={(e) => setEditData({...editData, applicant: {...editData.applicant, address: {...editData.applicant.address, state: e.target.value}}})}
                      />
                    </div>
                    <div className="data-row">
                      <span className="data-label">Postal Code</span>
                      <input
                        className="edit-input"
                        value={editData.applicant.address.postalCode}
                        onChange={(e) => setEditData({...editData, applicant: {...editData.applicant, address: {...editData.applicant.address, postalCode: e.target.value}}})}
                      />
                    </div>
                    <div className="data-row">
                      <span className="data-label">Country</span>
                      <input
                        className="edit-input"
                        value={editData.applicant.address.country}
                        onChange={(e) => setEditData({...editData, applicant: {...editData.applicant, address: {...editData.applicant.address, country: e.target.value}}})}
                      />
                    </div>
                    <div className="data-row">
                      <span className="data-label">Email</span>
                      <input
                        className="edit-input"
                        value={editData.applicant.email}
                        onChange={(e) => setEditData({...editData, applicant: {...editData.applicant, email: e.target.value}})}
                      />
                    </div>
                    <div className="data-row">
                      <span className="data-label">Phone</span>
                      <input
                        className="edit-input"
                        value={editData.applicant.phone}
                        onChange={(e) => setEditData({...editData, applicant: {...editData.applicant, phone: e.target.value}})}
                      />
                    </div>
                  </div>
                ) : applicant.applicantName ? (
                  <div className="data-grid">
                    <div className="data-row">
                      <span className="data-label">Name</span>
                      <span className="data-value">{applicant.applicantName}</span>
                      <button className="btn-copy-small" onClick={() => handleCopyText(applicant.applicantName)}>Copy</button>
                    </div>
                    {applicant.applicantType && (
                      <div className="data-row">
                        <span className="data-label">Type</span>
                        <span className="data-value">{applicant.applicantType}</span>
                      </div>
                    )}
                    {applicant.businessIdentifier && (
                      <div className="data-row">
                        <span className="data-label">{applicant.businessIdentifierType || 'Business ID'}</span>
                        <span className="data-value">{applicant.businessIdentifier}</span>
                        <button className="btn-copy-small" onClick={() => handleCopyText(applicant.businessIdentifier)}>Copy</button>
                      </div>
                    )}
                    <div className="data-row">
                      <span className="data-label">Address</span>
                      <span className="data-value">{formatAddress(applicant.address)}</span>
                      <button className="btn-copy-small" onClick={() => handleCopyText(formatAddress(applicant.address))}>Copy</button>
                    </div>
                    {applicant.email && (
                      <div className="data-row">
                        <span className="data-label">Email</span>
                        <span className="data-value">{applicant.email}</span>
                        <button className="btn-copy-small" onClick={() => handleCopyText(applicant.email)}>Copy</button>
                      </div>
                    )}
                    {applicant.phone && (
                      <div className="data-row">
                        <span className="data-label">Phone</span>
                        <span className="data-value">{applicant.phone}</span>
                        <button className="btn-copy-small" onClick={() => handleCopyText(applicant.phone)}>Copy</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="no-data">No applicant information available</p>
                )}
              </section>

              <section className="file-now-section">
                <h3>Client Contact</h3>
                <div className="data-grid">
                  <div className="data-row">
                    <span className="data-label">Client Name</span>
                    <span className="data-value">{application.clientName || 'Unknown'}</span>
                  </div>
                  {application.clientEmail && (
                    <div className="data-row">
                      <span className="data-label">Email</span>
                      <span className="data-value">{application.clientEmail}</span>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}

          {/* Classes Tab */}
          {activeTab === 'classes' && (
            <>
              {/* Edit mode: inline class/goods editing */}
              {isEditing && editData ? (
                <>
                  {editData.classification.selectedClasses.map((cls, classIdx) => (
                    <section key={classIdx} className="file-now-section">
                      <h3>
                        <input
                          className="edit-input edit-input-inline"
                          value={cls.classNumber}
                          onChange={(e) => {
                            const newClasses = [...editData.classification.selectedClasses];
                            newClasses[classIdx] = {...newClasses[classIdx], classNumber: e.target.value};
                            setEditData({...editData, classification: {...editData.classification, selectedClasses: newClasses}});
                          }}
                          placeholder="Class #"
                          style={{ width: '60px', display: 'inline-block', marginRight: '8px' }}
                        />
                        —
                        <input
                          className="edit-input edit-input-inline"
                          value={cls.className}
                          onChange={(e) => {
                            const newClasses = [...editData.classification.selectedClasses];
                            newClasses[classIdx] = {...newClasses[classIdx], className: e.target.value};
                            setEditData({...editData, classification: {...editData.classification, selectedClasses: newClasses}});
                          }}
                          placeholder="Class name"
                          style={{ width: '200px', display: 'inline-block', marginLeft: '8px' }}
                        />
                        <button
                          className="btn-remove-class"
                          onClick={() => removeClass(classIdx)}
                          title="Remove this entire class"
                        >
                          ✕ Remove Class
                        </button>
                      </h3>
                      <div className="goods-services-list edit-mode">
                        {cls.goodsServices.map((item, i) => (
                          <div key={i} className="goods-item edit-mode">
                            <p className="goods-text">{item}</p>
                            <button
                              className="btn-remove-small"
                              onClick={() => removeServiceFromClass(classIdx, i)}
                            >
                              ✕ Remove
                            </button>
                          </div>
                        ))}
                        <div className="goods-add-row">
                          <input
                            className="edit-input"
                            placeholder="+ Add service..."
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addServiceToClass(classIdx, e.target.value);
                                e.target.value = '';
                              }
                            }}
                          />
                          <button
                            className="btn-add-small"
                            onClick={(e) => {
                              const input = e.target.previousSibling;
                              addServiceToClass(classIdx, input.value);
                              input.value = '';
                            }}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </section>
                  ))}
                  <button className="btn-add-class" onClick={addNewClass}>
                    + Add New Class
                  </button>
                </>
              ) : classification.selectedClasses?.length > 0 ? (
                classification.selectedClasses.map((cls, idx) => (
                  <section key={idx} className="file-now-section">
                    <h3>
                      Class {cls.classNumber || cls.number || cls.class}
                      {cls.className || cls.name ? ` — ${cls.className || cls.name}` : ''}
                    </h3>
                    {(cls.goodsServices?.length > 0) ? (
                      <div className="goods-services-list">
                        {cls.goodsServices.map((item, i) => {
                          const text = typeof item === 'string' ? item : item.description || item.term || JSON.stringify(item);
                          return (
                            <div key={i} className="goods-item">
                              <p className="goods-text">{text}</p>
                              <button
                                className="btn-copy-small"
                                onClick={() => handleCopyText(text)}
                              >
                                Copy
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="no-data">No goods/services specified for this class</p>
                    )}
                  </section>
                ))
              ) : (
                <>
                  {/* Legacy format: flat niceClasses + separate goodsServices */}
                  <section className="file-now-section">
                    <h3>Nice Classes</h3>
                    {classification.niceClasses?.length > 0 ? (
                      <div className="classes-list">
                        {classification.niceClasses.map((classNum) => (
                          <div key={classNum} className="class-item">
                            <span className="class-number">Class {classNum}</span>
                            {classification.classDescriptions?.[classNum] && (
                              <p className="class-description">
                                {classification.classDescriptions[classNum]}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-data">No classes specified</p>
                    )}
                  </section>

                  <section className="file-now-section">
                    <h3>Goods & Services</h3>
                    {classification.goodsServices?.length > 0 ? (
                      <div className="goods-services-list">
                        {classification.goodsServices.map((item, index) => (
                          <div key={index} className="goods-item">
                            <p className="goods-text">{typeof item === 'string' ? item : item.description || JSON.stringify(item)}</p>
                            <button
                              className="btn-copy-small"
                              onClick={() => handleCopyText(typeof item === 'string' ? item : item.description)}
                            >
                              Copy
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-data">No goods/services descriptions available</p>
                    )}
                  </section>
                </>
              )}
            </>
          )}

          {/* All Info Tab */}
          {activeTab === 'all' && (
            <>
              {/* Trademark Section */}
              <section className="file-now-section">
                <h3>Trademark</h3>
                <div className="all-info-grid">
                  <div className="all-info-item">
                    <span className="all-info-label">Name</span>
                    <span className="all-info-value">{trademark.name || 'Not provided'}</span>
                  </div>
                  <div className="all-info-item">
                    <span className="all-info-label">Type</span>
                    <span className="all-info-value">{trademark.type || 'Figurative'}</span>
                  </div>
                  {trademark.imageUrl && (
                    <div className="all-info-item all-info-item-full">
                      <span className="all-info-label">Image</span>
                      <img src={trademark.imageUrl} alt={trademark.name} className="all-info-image" />
                    </div>
                  )}
                </div>
              </section>

              {/* Applicant Section */}
              <section className="file-now-section">
                <h3>Applicant</h3>
                <div className="all-info-grid">
                  <div className="all-info-item">
                    <span className="all-info-label">Name</span>
                    <span className="all-info-value">{applicant.applicantName || application.clientName || 'Not provided'}</span>
                  </div>
                  <div className="all-info-item">
                    <span className="all-info-label">Type</span>
                    <span className="all-info-value">{applicant.applicantType || 'Not specified'}</span>
                  </div>
                  {applicant.businessIdentifier && (
                    <div className="all-info-item">
                      <span className="all-info-label">{applicant.businessIdentifierType || 'Business ID'}</span>
                      <span className="all-info-value">{applicant.businessIdentifier}</span>
                    </div>
                  )}
                  <div className="all-info-item all-info-item-full">
                    <span className="all-info-label">Address</span>
                    <span className="all-info-value">{formatAddress(applicant.address)}</span>
                  </div>
                  <div className="all-info-item">
                    <span className="all-info-label">Email</span>
                    <span className="all-info-value">{applicant.email || application.clientEmail || 'Not provided'}</span>
                  </div>
                  <div className="all-info-item">
                    <span className="all-info-label">Phone</span>
                    <span className="all-info-value">{applicant.phone || 'Not provided'}</span>
                  </div>
                </div>
              </section>

              {/* Classification Section */}
              <section className="file-now-section">
                <h3>Classification</h3>
                <div className="all-info-grid">
                  <div className="all-info-item">
                    <span className="all-info-label">Nice Classes</span>
                    <span className="all-info-value">
                      {classification.niceClasses?.length > 0
                        ? classification.niceClasses.join(', ')
                        : 'Not specified'}
                    </span>
                  </div>
                  <div className="all-info-item">
                    <span className="all-info-label">Countries</span>
                    <span className="all-info-value">
                      {classification.countries?.length > 0
                        ? classification.countries.map(c => c.code || c.name || c).join(', ')
                        : 'Not specified'}
                    </span>
                  </div>
                </div>
                {classification.selectedClasses?.length > 0 && (
                  <div className="all-info-classes">
                    <h4>Class Details</h4>
                    {classification.selectedClasses.map((cls, idx) => (
                      <div key={idx} className="all-info-class-item">
                        <strong>Class {cls.classNumber || cls.number || cls.class || cls}{cls.className || cls.name ? ` — ${cls.className || cls.name}` : ''}</strong>
                        {cls.description && <p>{cls.description}</p>}
                        {Array.isArray(cls.goodsServices) ? (
                          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                            {cls.goodsServices.map((gs, i) => (
                              <li key={i} style={{ fontSize: '13px', color: '#555' }}>
                                {typeof gs === 'string' ? gs : gs.term || gs.description || JSON.stringify(gs)}
                              </li>
                            ))}
                          </ul>
                        ) : cls.goodsServices ? (
                          <p>{cls.goodsServices}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Analysis Section */}
              {application.analysis && (
                <section className="file-now-section">
                  <h3>Risk Analysis</h3>
                  <div className="all-info-grid">
                    <div className="all-info-item">
                      <span className="all-info-label">Risk Level</span>
                      <span className={`all-info-value risk-${application.analysis.riskLevel?.toLowerCase()}`}>
                        {application.analysis.riskLevel || 'Not assessed'}
                      </span>
                    </div>
                    <div className="all-info-item all-info-item-full">
                      <span className="all-info-label">Summary</span>
                      <span className="all-info-value">{application.analysis.summary || 'No summary available'}</span>
                    </div>
                  </div>
                </section>
              )}

              {/* Application Meta */}
              <section className="file-now-section">
                <h3>Application Details</h3>
                <div className="all-info-grid">
                  <div className="all-info-item">
                    <span className="all-info-label">Application ID</span>
                    <span className="all-info-value">{application.applicationId}</span>
                  </div>
                  <div className="all-info-item">
                    <span className="all-info-label">Status</span>
                    <span className="all-info-value">{application.status}</span>
                  </div>
                  <div className="all-info-item">
                    <span className="all-info-label">Created</span>
                    <span className="all-info-value">
                      {application.createdAt ? new Date(application.createdAt).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                  <div className="all-info-item">
                    <span className="all-info-label">Payment Date</span>
                    <span className="all-info-value">
                      {application.paymentCompletedAt ? new Date(application.paymentCompletedAt).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                </div>
              </section>
            </>
          )}

          {/* View Mode - Show filing details */}
          {viewMode && (
            <section className="file-now-section file-now-section-form">
              <h3>Filing Details</h3>
              <div className="data-grid">
                <div className="data-row">
                  <span className="data-label">Status</span>
                  <span className="data-value" style={{ color: 'var(--success, #10b981)', fontWeight: 600 }}>
                    ✅ Filed
                  </span>
                </div>
                <div className="data-row">
                  <span className="data-label">IP Office Ref</span>
                  <span className="data-value">{jurisdiction.ipOfficeRef || 'Not recorded'}</span>
                  {jurisdiction.ipOfficeRef && (
                    <button className="btn-copy-small" onClick={() => handleCopyText(jurisdiction.ipOfficeRef)}>Copy</button>
                  )}
                </div>
                {jurisdiction.filedAt && (
                  <div className="data-row">
                    <span className="data-label">Filed Date</span>
                    <span className="data-value">{new Date(jurisdiction.filedAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
              <div className="file-now-modal-actions" style={{ marginTop: '16px' }}>
                <button type="button" className="btn-cancel" onClick={onClose}>
                  Close
                </button>
              </div>
            </section>
          )}

          {/* Pending approval warning */}
          {!viewMode && application.amendmentStatus === 'pending_approval' && activeTab !== 'email' && activeTab !== 'history' && (
            <section className="file-now-section amendment-pending-banner">
              <p>⏳ <strong>Awaiting Customer Approval</strong> — This application has pending amendments that the customer has not yet approved. Filing should wait until they confirm.</p>
            </section>
          )}
          {!viewMode && application.amendmentStatus === 'query_received' && activeTab !== 'email' && activeTab !== 'history' && (
            <section className="file-now-section amendment-pending-banner amendment-query-banner">
              <p>❓ <strong>Customer Has a Question</strong> — The customer responded with a question about the amendments. Please resolve before filing.</p>
            </section>
          )}

          {/* Mark as Filed - Only show when not in view mode */}
          {!viewMode && activeTab !== 'email' && activeTab !== 'history' && (
            <section className="file-now-section file-now-section-form">
              <h3>Mark as Filed</h3>
              <p className="section-description">
                After submitting at the IP Office, enter the reference number you received:
              </p>
              <form onSubmit={handleSubmit}>
                <input
                  type="text"
                  value={ipOfficeRef}
                  onChange={(e) => setIpOfficeRef(e.target.value)}
                  placeholder={jurisdictionData?.referenceFormat?.example || 'e.g., TM2025/12345'}
                  className="ip-office-ref-input"
                />
                <div className="file-now-modal-actions">
                  <button type="button" className="btn-cancel" onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-mark-filed"
                    disabled={!ipOfficeRef.trim() || isLoading || application.amendmentStatus === 'pending_approval' || application.amendmentStatus === 'query_received'}
                  >
                    {isLoading ? 'Marking...' : application.amendmentStatus === 'pending_approval' ? 'Awaiting Approval...' : application.amendmentStatus === 'query_received' ? 'Query Pending...' : 'Mark as Filed'}
                  </button>
                </div>
              </form>
            </section>
          )}

          {/* ✉ Send to Customer Tab */}
          {activeTab === 'email' && canEdit && (
            <section className="file-now-section">
              <h3>✉ Send to Customer</h3>
              {emailSent ? (
                <div className="amendment-success">
                  <p style={{ fontSize: '18px', color: '#059669', fontWeight: 600 }}>✓ Email sent successfully</p>
                  <p>The customer has been notified about the changes.</p>
                  <button className="btn-cancel" onClick={() => setEmailSent(false)} style={{ marginTop: '12px' }}>
                    Send Another
                  </button>
                </div>
              ) : (
                <>
                  <div className="email-compose-field">
                    <label>To:</label>
                    <span className="email-compose-to">{application.clientEmail || 'Customer email'}</span>
                  </div>
                  <div className="email-compose-field">
                    <label>Subject:</label>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Update on your trademark application"
                      className="email-compose-input"
                    />
                  </div>
                  <div className="email-compose-field">
                    <label>Body:</label>
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Hi {firstName},&#10;&#10;I've completed my review of your trademark application..."
                      className="email-compose-textarea"
                      rows={10}
                    />
                    <p className="email-compose-hint">Use {'{firstName}'} and {'{trademarkName}'} as placeholders. They'll be replaced automatically.</p>
                  </div>

                  <div className="email-compose-options">
                    <label className="email-compose-checkbox">
                      <input
                        type="checkbox"
                        checked={includeChangesSummary}
                        onChange={(e) => setIncludeChangesSummary(e.target.checked)}
                      />
                      Include changes summary block (auto-generated)
                    </label>
                    <label className="email-compose-checkbox">
                      <input
                        type="checkbox"
                        checked={includeApprovalButtons}
                        onChange={(e) => setIncludeApprovalButtons(e.target.checked)}
                      />
                      Include approval buttons (Approve / Ask a Question)
                    </label>
                  </div>

                  <div className="email-compose-signature">
                    <p className="signature-label">Signature (auto-appended):</p>
                    <div className="signature-preview">
                      <strong>Ken McInnes</strong><br />
                      <span>Head of Trademark Strategy</span><br />
                      <span>Registered Patent & Trade Marks Attorney</span><br />
                      <br />
                      <span>📞 +61 2 8267 7300 | ✉ support@markotm.com</span><br />
                      <span>📍 Sydney | Madrid | London</span>
                    </div>
                  </div>

                  <div className="file-now-modal-actions" style={{ marginTop: '16px' }}>
                    <button type="button" className="btn-cancel" onClick={onClose}>
                      Cancel
                    </button>
                    <button
                      className="btn-mark-filed"
                      onClick={handleSendEmail}
                      disabled={isSendingEmail || !emailBody.trim()}
                    >
                      {isSendingEmail ? 'Sending...' : 'Send Email & Notify Customer'}
                    </button>
                  </div>
                </>
              )}
            </section>
          )}

          {/* 📋 History Tab */}
          {activeTab === 'history' && (
            <section className="file-now-section">
              <h3>📋 Change History</h3>
              {(application.amendmentHistory && application.amendmentHistory.length > 0) ? (
                <div className="amendment-history-list">
                  {[...application.amendmentHistory].reverse().map((entry, idx) => (
                    <div key={entry.id || idx} className="amendment-history-item">
                      <div className="amendment-history-header">
                        <span className="amendment-history-dot">●</span>
                        <span className="amendment-history-date">
                          {new Date(entry.changedAt).toLocaleDateString()} — {entry.changedByName || 'Unknown'}
                        </span>
                      </div>
                      <div className="amendment-history-body">
                        {entry.changes && entry.changes.length > 0 && (
                          <p className="amendment-history-changes">
                            Changed: {entry.changes.map(c => `${c.section}.${c.field}`).join(', ')}
                          </p>
                        )}
                        {entry.note && (
                          <p className="amendment-history-note">Note: "{entry.note}"</p>
                        )}
                        <div className="amendment-history-status">
                          {entry.customerNotified && <span className="badge badge-sent">Emailed ✓</span>}
                          {entry.customerResponse === 'approved' && <span className="badge badge-approved">Approved ✓</span>}
                          {entry.customerResponse === 'query' && <span className="badge badge-query">Query received</span>}
                          {entry.customerNotified && !entry.customerResponse && <span className="badge badge-pending">Awaiting response</span>}
                          {!entry.customerNotified && <span className="badge badge-draft">Not sent</span>}
                        </div>
                        {entry.customerMessage && (
                          <p className="amendment-history-message">Customer: "{entry.customerMessage}"</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">No amendment history yet</p>
              )}

              {/* Show assignment history too */}
              {application.assignment && (
                <div className="amendment-history-item" style={{ marginTop: '16px', opacity: 0.7 }}>
                  <div className="amendment-history-header">
                    <span className="amendment-history-dot">●</span>
                    <span className="amendment-history-date">
                      {application.assignment.assignedAt
                        ? new Date(application.assignment.assignedAt).toLocaleDateString()
                        : 'Unknown date'} — System
                    </span>
                  </div>
                  <div className="amendment-history-body">
                    <p className="amendment-history-changes">
                      Application assigned to {application.assignment.attorneyName || 'attorney'}
                    </p>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ⏳ Pending Changes Tab */}
          {activeTab === 'pending' && (
            <section className="file-now-section">
              <h3>⏳ Pending Changes</h3>
              <p className="section-description">
                These changes are awaiting customer approval before filing can proceed.
              </p>

              {/* Show pending amendments */}
              {(application.amendmentHistory && application.amendmentHistory.length > 0) ? (
                <div className="pending-changes-list">
                  {application.amendmentHistory
                    .filter(entry => !entry.customerResponse || entry.customerResponse !== 'approved')
                    .map((entry, idx) => (
                      <div key={entry.id || idx} className="pending-change-item">
                        <div className="pending-change-header">
                          <span className="pending-change-date">
                            {new Date(entry.changedAt).toLocaleDateString()} — {entry.changedByName || 'Unknown'}
                          </span>
                          {entry.customerNotified && !entry.customerResponse && (
                            <span className="badge badge-pending">Emailed, awaiting response</span>
                          )}
                          {entry.customerResponse === 'query' && (
                            <span className="badge badge-query">Customer has a question</span>
                          )}
                          {!entry.customerNotified && (
                            <span className="badge badge-draft">Not yet sent to customer</span>
                          )}
                        </div>
                        {entry.note && (
                          <p className="pending-change-note">"{entry.note}"</p>
                        )}
                        {entry.changes && entry.changes.length > 0 && (
                          <ul className="pending-change-details">
                            {entry.changes.map((c, i) => (
                              <li key={i}>
                                <strong>{c.field}:</strong>{' '}
                                {typeof c.oldValue === 'string' ? (
                                  <span><s style={{color:'#9ca3af'}}>{c.oldValue}</s> → {c.newValue}</span>
                                ) : (
                                  <span>updated</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                        {entry.customerMessage && (
                          <div className="pending-change-customer-msg">
                            <strong>Customer message:</strong> "{entry.customerMessage}"
                          </div>
                        )}
                        {/* Per-amendment approve button */}
                        {canEdit && (
                          <button
                            className="btn-approve-single"
                            onClick={() => approveMutation.mutate({ applicationId: application.id, amendmentId: entry.id })}
                            disabled={approveMutation.isPending}
                          >
                            {approveMutation.isPending ? 'Approving...' : '✓ Approve'}
                          </button>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <p className="no-data">No pending changes</p>
              )}

              {/* Bulk Approve All Button */}
              {canEdit && application.amendmentHistory && application.amendmentHistory.filter(e => !e.customerResponse || e.customerResponse !== 'approved').length > 1 && (
                <div className="pending-approve-section">
                  <button
                    className="btn-mark-filed"
                    onClick={() => approveMutation.mutate({ applicationId: application.id })}
                    disabled={approveMutation.isPending}
                  >
                    {approveMutation.isPending ? 'Approving...' : '✓ Approve All Changes'}
                  </button>
                </div>
              )}
            </section>
          )}

          {/* Edit mode save bar */}
          {isEditing && activeTab !== 'email' && activeTab !== 'history' && activeTab !== 'pending' && (
            <section className="file-now-section file-now-section-form amendment-save-bar">
              <div className="email-compose-field">
                <label>Note (optional):</label>
                <input
                  type="text"
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  placeholder="e.g., Corrected applicant name to match ABN registration"
                  className="email-compose-input"
                />
              </div>
              <div className="file-now-modal-actions">
                <button type="button" className="btn-cancel" onClick={cancelEditing}>
                  Cancel
                </button>
                <button
                  className="btn-mark-filed"
                  onClick={saveAmendments}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default FileNowModal;
